import Vue, { VNode } from 'vue';
import api from '@/rest-api';
import {
  IngestTextFragment, Label, LabelAssignment, Suggestion, MlSuggestion, MlLabelSuggestionItem,
  MlThemePrediction, LabelPickerAdvancedLabelStatus,
} from '@/model';
import LabelPickerAdvanced from '@/components/LabelPickerAdvanced.vue';
import deepEqual from '@/util/deep-equals';
import labels, { findParentsOfLabelRelation, labelSorter } from '@/store/labels';
import { mlSuggestionTypesForThemes } from '@/store/ui-config';
import { notEmpty } from '@/util/array-util';

export default Vue.extend({
  name: 'LabelPickerAdvancedController',
  components: { LabelPickerAdvanced },

  props: {
    value: { type: Object as () => IngestTextFragment, required: true },
    documentId: { type: Number as () => number },
    ingestId: { type: Number as () => number },
    documentLabels: { type: Array as () => number[], default: () => [] },
    intersectingLabels: { type: Array as () => number[], default: () => [] },
    labelStatus: { type: Object as () => LabelPickerAdvancedLabelStatus, required: true },
    // ml label suggestions are now loaded by the parent (FragmentsEditor) so that request
    // dispatch order relative to other ml requests (e.g. semantic search) can be controlled.
    rawMlSuggestions: { type: Array as () => readonly MlLabelSuggestionItem[], default: () => [] },
    mlPredictedThemes: { type: Array as () => readonly MlThemePrediction[], default: () => [] },
    isLoadingPotential: { type: Boolean, default: false },
  },

  data() {
    return {
      suggestions: [] as Suggestion[],
      isLoadingContext: false,
      contextRequestId: 0,
      contextLabels: [] as Label[],
      recentlyUsed: [] as number[],
      // pinned labels are configured in the simple label picker's settings;
      // loaded here so they're known before the filters mount and apply defaults
      pinned: (JSON.parse(localStorage.getItem('labelPicker.settings') ?? '{}').pinned ?? []) as number[],
    };
  },

  mounted() {
    const recentlyUsed = localStorage.getItem('labelPickerAdvanced.recentlyUsed');
    if (recentlyUsed) {
      this.recentlyUsed = JSON.parse(recentlyUsed);
    }
  },

  watch: {
    ingestSuggestionsInput: {
      immediate: true,
      async handler(to: { ingestId: number; boxIds: ReadonlyArray<number> }, from) {
        if (!deepEqual(to, from)) {
          await this.loadIngestSuggestions(to.ingestId, to.boxIds);
        }
      },
    },
    selectedLabelIds: {
      immediate: true,
      async handler(to: number[], from?: number[]) {
        if (!deepEqual(to, from)) {
          await this.loadContext();
        }
      },
    },
    value: {
      handler(to: IngestTextFragment, from: IngestTextFragment) {
        if (!deepEqual(to.labels, from.labels)) {
          const nv = to.labels
            .map((id) => id)
            .filter((la) => la.labelId > 0);
          this.recentlyUsed = [...new Set([...nv.map((la) => la.labelId), ...this.recentlyUsed])].slice(
            0,
            50,
          );
        }
      },
      deep: true,
    },
    recentlyUsed: {
      handler(to, from) {
        if (!deepEqual(to, from)) {
          localStorage.setItem('labelPickerAdvanced.recentlyUsed', JSON.stringify(this.recentlyUsed));
        }
      },
      deep: true,
    },
  },

  computed: {
    labelStatusWithRecentlyUsed(): LabelPickerAdvancedLabelStatus {
      return {
        ...this.labelStatus,
        recentlyUsed: this.recentlyUsed,
        pinned: this.pinned,
      };
    },
    ingestSuggestionsInput(): { ingestId: number; boxIds: ReadonlyArray<number> } {
      return { ingestId: this.ingestId, boxIds: this.value.boxes.map((b) => b.boxId) };
    },
    ingestSuggestions(): number[] {
      return this.suggestions.map((s) => s.labelId).filter((id) => !this.documentLabels.includes(id));
    },
    selectedLabelIds(): number[] {
      return this.value.labels.map((s) => s.labelId);
    },
    activeThemeCodes(): string[] {
      const fromModel = this.mlPredictedThemes
        .filter((t) => t.active)
        .map((t) => labels.getters.lookup.get(t.labelId)?.code);
      const fromSelection = this.selectedLabelIds
        .map((id) => labels.getters.lookup.get(id))
        .filter((l) => l?.type === 'Theme')
        .map((l) => l?.code);
      return [...new Set([...fromModel, ...fromSelection].filter(notEmpty))];
    },
    mlSuggestions(): Map<number, MlSuggestion> {
      const types = mlSuggestionTypesForThemes(this.activeThemeCodes);
      const visibleTypes = types === null ? null : new Set(types);
      const entries: Array<[number, MlSuggestion]> = [
        // Themes are always shown — they're the model's own top-level prediction, not gated by a pool.
        ...this.mlPredictedThemes
          .filter((t) => t.active)
          .map((t): [number, MlSuggestion] => [t.labelId, {
            type: 'ml',
            confidence: Math.round(t.score * 100),
          }]),
        ...this.rawMlSuggestions
          .filter((s) => visibleTypes === null || visibleTypes.has(s.type))
          .map((s): [number, MlSuggestion] => [s.labelId, {
            type: 'ml',
            confidence: Math.round(s.score * 100),
          }]),
      ];
      return new Map(entries.filter(([labelId]) => !this.documentLabels.includes(labelId)));
    },
    isBusy(): boolean {
      return this.isLoadingContext || this.isLoadingPotential;
    },
  },

  methods: {
    togglePin(labelId: number) {
      this.pinned = this.pinned.includes(labelId)
        ? this.pinned.filter((id) => id !== labelId)
        : [...this.pinned, labelId];
      const settings = JSON.parse(localStorage.getItem('labelPicker.settings') ?? '{}');
      localStorage.setItem('labelPicker.settings', JSON.stringify({ ...settings, pinned: this.pinned }));
    },
    async loadIngestSuggestions(ingestId: number, boxIds: ReadonlyArray<number>) {
      this.suggestions = (await api.ingestSuggestions(ingestId)).filter((s) => boxIds.includes(s.boxId));
    },
    labelIdsForSuggestions(): number[] {
      const publishLabel = labels.getters.type('Workflow').get('Publish');
      const qaLabel = labels.getters.type('Workflow').get('QA');
      return [
        ...(publishLabel ? [publishLabel.id] : []),
        ...this.selectedLabelIds,
      ].filter((l) => !qaLabel || l !== qaLabel.id);
    },
    async loadContext() {
      this.isLoadingContext = true;

      const toSearchLabels = this.labelIdsForSuggestions();
      this.contextLabels = (await api.contextLabels(toSearchLabels))
        .map((l) => labels.getters.lookup.get(l))
        .filter(notEmpty);
      this.isLoadingContext = false;
    },
  },

  render(create): VNode {
    return create(
      LabelPickerAdvanced, {
        props: {
          ingestSuggestions: this.ingestSuggestions,
          value: this.value.labels,
          documentId: this.documentId,
          documentLabels: this.documentLabels,
          intersectingLabels: this.intersectingLabels,
          mlSuggestions: this.mlSuggestions,
          contextLabels: this.contextLabels,
          isBusy: this.isBusy,
          labelStatus: this.labelStatusWithRecentlyUsed,
        },
        on: {
          togglePin: (id: number) => this.togglePin(id),
          input: (v) => this.$emit('input', ({
            ...this.value,
            labels: v,
          })),
        },
      },
    );
  },
});

export const currentLabelStatus = (
  original: readonly LabelAssignment[],
  current: readonly LabelAssignment[],
  recentlyUsed?: readonly number[],
): LabelPickerAdvancedLabelStatus => {
  const originalLabelsWithEdges = original
    .map((id) => labels.getters.lookupWithEdges.get(id.labelId))
    .filter(notEmpty)
    .sort(labelSorter(['type', 'length', 'alpha']));
  const added = current.filter((la) => !original.find((ola) => ola.labelId === la.labelId));
  const addedLabelsWithEdges = added
    .map((id) => labels.getters.lookupWithEdges.get(id.labelId))
    .filter(notEmpty);
  const currentLabelsWithEdges = [
    ...originalLabelsWithEdges,
    ...addedLabelsWithEdges,
  ];
  const parents = findParentsOfLabelRelation(currentLabelsWithEdges, ['Product', 'Transaction']);
  return {
    saved: original,
    added,
    removed: original.filter((la) => !current.find((ola) => ola.labelId === la.labelId)),
    parents,
    recentlyUsed,
  };
};
